import type { Palette, PaletteColor } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { lighten, sliderClasses } from '@mui/material';
import { createUseStyles } from 'react-jss';

export const theme = {
  colors: {
    'blue-100': '#155181',
    'blue-80': '#8AA8C0', // was #15518180
    'blue-55': '#B1C5D5', // was #15518155
    'blue-30': '#D3DEE7', // was #15518130
    'blue-10': '#F0F4F7', // was #15518110
    'green-100': '#008b7f',
    'green-100-lighter': '#a1c4bf', // hover state
    'green-100-light': '#91bab4', // normal state
    'green-90': '#128478',
    'green-80': '#7FC5BF', // was #008b7f80
    'green-55': '#AAD8D4', // was #008b7f55
    'green-30': '#CFE9E7', // was #008b7f30
    'green-10': '#EFF8F7', // was #008b7f10
    'grey-100': '#e4e5e6',
    'grey-80': '#F1F2F2', // was #e4e5e680
    'grey-55': '#F6F6F7', // was #e4e5e655
    'grey-30': '#FAFAFA', // was #e4e5e630
    'grey-20': '#e2e2e2',
    'grey-10': '#f7f7f7',
    'red-55': '#e0746f',
    'yellow-100': '#F4B432',
    'yellow-100-lighter': '#e7d6ab', // hover state
    'yellow-100-light': '#e8d19c', // normal state
    'purple-100': '#9E1242',
    'pale-orange': '#F5A67A',
    'pale-orange-lighter': '#e7d1bf', // hover state
    'pale-orange-light': '#e7cab5', // normal state
    'blue-study': '#4BABD6',
    'blue-study-lighter': '#b2d1df', // hover state
    'blue-study-light': '#a5cadd', // normal state
    coral: '#DE706B',
    'heatmap-incidence': '#6aaed5',
    'heatmap-mortality': '#fc694b',
    'heatmap-nccp': '#008b7f',
    sand: '#ece1cb',
    'sand-lighter': '#f7f1e6', // hover state
    'sand-light': '#f2ead9', // normal state
    'nccp-available': '#b2dfdb',
    'nccp-lung-cancer': '#4db6ac',
    'nccp-early-detection': '#008b7f',
    'nccp-funding': '#00695c',
    white: '#ffffff',
    'white-lighter': '#eff3f2', // hover state
    'white-light': '#f2f5f4', // normal state
    link: '#1976d2', // MUI primary blue, used for interactive links
    default: '#5a5a5a',
    darker: '#6a6a6a',
  },
  fontFamilies: {
    default: "'Be Vietnam Pro', sans-serif",
  },
  fontSizes: {
    h1: '2.25rem', // 36px
    h2: '1.625rem', // 26px
    h3: '1.125rem', // 18px
    h4: '1rem', // 16px
    p1: '1rem', // 16px
    p2: '0.875rem', // 14px
    p3: '0.75rem', // 12px
    p4: '0.5rem', // 8px
  },
  breakPoints: {
    tablet: 580,
    laptop: 850,
    desktop: 1050,
    widescreen: 1450,
  },
  layout: {
    drawerWidth: '40%',
  },
  /**
   * Spacing function that returns rem values.
   * Base unit: 0.25rem (4px at default 16px root)
   *
   * @example
   * theme.spacing(4)     // '1rem' (16px)
   * theme.spacing(2)     // '0.5rem' (8px)
   * theme.spacing(2, 4)  // '0.5rem 1rem' (8px 16px)
   */
  spacing: (...factors: number[]): string =>
    factors.map(f => `${0.25 * f}rem`).join(' '),
};

export type LcpnTheme = typeof theme;

export const useFontStyles = createUseStyles((_: LcpnTheme): unknown => ({
  '@global': {
    '@font-face': [
      {
        fontFamily: 'Be Vietnam Pro',
        src: `url("${process.env.PUBLIC_URL}/fonts/BeVietnamPro/BeVietnamPro-Regular.ttf") format('TrueType')`,
        fontWeight: 400,
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Be Vietnam Pro Medium',
        src: `url("${process.env.PUBLIC_URL}/fonts/BeVietnamPro/BeVietnamPro-Medium.ttf") format('TrueType')`,
        fontWeight: 500,
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Be Vietnam Pro Italic',
        src: `url("${process.env.PUBLIC_URL}/fonts/BeVietnamPro/BeVietnamPro-Italic.ttf") format('TrueType')`,
        fontWeight: 500,
        fontDisplay: 'swap',
      },
    ],
  },
}));

export const useResetStyles = createUseStyles((theme: LcpnTheme) => ({
  '@global': {
    html: {
      fontSize: '85%',
    },
    '@media (pointer: fine)': {
      html: {
        fontSize: '85%',
      },
    },
    '@media (pointer: coarse)': {
      html: {
        fontSize: '100%',
      },
    },
    body: {
      fontFamily: theme.fontFamilies.default,
      color: '#5a5a5a',
      margin: 0,
    },
    'h1, h2, h3, h4, h5, h6, p, dl ,dd': {
      margin: 0,
      padding: 0,
    },
    'p + p': {
      marginTop: '1em',
    },
    ul: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
      color: theme.colors.default,
    },
    li: {
      marginBottom: theme.spacing(2),
    },
    button: {
      border: 'none',
      backgroundColor: 'transparent',
      outline: 'none',
    },
    // Global keyboard focus styles for accessibility
    // Uses :focus-visible to only show for keyboard navigation, not mouse clicks
    // To disable: comment out this entire block
    'button:focus-visible, a:focus-visible, [tabindex]:focus-visible': {
      outline: `2px solid ${theme.colors['blue-100']}`,
      outlineOffset: '2px',
      borderRadius: '2px',
    },
    '@media print': {
      '.tooltip-trigger': {
        textDecoration: 'none !important',
        cursor: 'auto !important',
      },
    },
  },
}));

const sliderThumbBoxShadow =
  '0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)';

export const muiTheme = createTheme({
  // MUI default: 8px per unit = 0.5rem base
  // This matches MUI's internal component expectations (buttons, etc.)
  // Note: LCPN theme uses 0.25rem (4px) base, so multipliers differ
  spacing: (factor: number) => `${0.5 * factor}rem`,

  palette: {
    primary: {
      main: theme.colors['green-100'],
      light: lighten(theme.colors['green-100'], 0.2),
      lighter: lighten(theme.colors['green-100'], 0.4),
      lightest: lighten(theme.colors['green-100'], 0.6),
    },
    secondary: {
      main: theme.colors['blue-100'],
    },
    error: {
      main: '#F04F4F',
      contrastText: '#fff',
    },
    warning: {
      main: '#F0B04F',
    },
    success: {
      main: '#3AD78C',
      contrastText: '#fff',
    },
    text: {
      primary: '#333740',
      secondary: '#5a5a5a',
    },
    common: {
      blue: '#3300FF',
    },
  },

  shape: {
    borderRadius: 8,
  },

  typography: {
    fontFamily: "'Be Vietnam Pro', sans-serif",
    h1: {
      fontSize: '3rem', // 48px
      fontWeight: 700,
    },
    h2: {
      fontSize: '1.625rem', // 26px
      fontWeight: 700,
    },
    h3: {
      fontSize: '1.375rem', // 22px
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.125rem', // 18px
      fontWeight: 500,
      '& > b': {
        fontWeight: 700,
      },
    },
    h5: {
      fontSize: '1rem', // 16px
      fontWeight: 700,
    },
    h6: {
      fontSize: '0.875rem', // 14px
      fontWeight: 700,
    },
    body1: {
      fontSize: '1rem', // 16px
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
    },
    button: {
      fontSize: '0.875rem', // 14px
      textTransform: 'none',
    },
  },

  components: {
    MuiSlider: {
      styleOverrides: {
        root: ({ ownerState, theme }) =>
          theme.unstable_sx({
            '& .MuiSlider-thumb': {
              height: 28,
              width: 28,
              backgroundColor: '#fff',
              boxShadow: sliderThumbBoxShadow,
              '&:focus, &:hover, &.Mui-active': {
                boxShadow: sliderThumbBoxShadow,
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                  boxShadow: sliderThumbBoxShadow,
                },
              },
            },
            [`& .${sliderClasses.markLabel}`]: {
              py: 0.5,
              px: 1,
              typography: {
                fontSize: '0.625rem', // 10px
              },
            },
          }),
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
      },
      styleOverrides: {
        root: ({ ownerState, theme }) =>
          theme.unstable_sx({
            fontWeight: 400,
            boxShadow: 'none',
            py: 1,
            px: 2,
            ':hover': {
              boxShadow: 'none',
            },
            borderRadius: 32,
            lineHeight: '1.2rem',
            ...(ownerState.size === 'small' && {
              py: 1,
              lineHeight: '1rem',
            }),
          }),

        text: ({ ownerState = {}, theme }) => {
          const c =
            ownerState &&
            theme.palette[(ownerState.color || 'primary') as keyof Palette];

          return {
            ':hover': {
              opacity: c ? 1 : 0.8,
            },
          };
        },
        contained: ({ ownerState = {}, theme }) => {
          const c =
            ownerState &&
            theme.palette[(ownerState.color || 'primary') as keyof Palette];

          return {
            ':hover': {
              backgroundColor: theme.palette[
                (ownerState.color || 'primary') as keyof Palette
              ]
                ? lighten((c as PaletteColor).main ?? ownerState.color, 0.2)
                : ownerState.color,
              opacity: c ? 1 : 0.8,
            },
          };
        },
      },
    },
  },
});

declare module '@mui/material/styles' {
  interface CommonColors {
    blue: string;
  }
  interface PaletteColor {
    lighter?: string;
    lightest?: string;
  }
  interface SimplePaletteColorOptions {
    lighter?: string;
    lightest?: string;
  }
}
